<template>
    <div>
        <h2 class="mb-4">{{ chart.title }}</h2>
        <div class="flex flex-wrap mb-24">
            <span v-for="tag in sortedTags" class="text-white tag" :data-name="tag.name" :key="tag.name">{{ tag.name }}</span>
        </div>

        <div v-if="isLoading" class="flex items-center justify-center w-full min-h-200"><img src="/images/spinner.gif" alt="Loading"></div>

        <div v-else>
            <div v-if="hasDataError" class="mb-24 alert bg-coral-darkest">
                <span class="font-bold">Error:</span> {{ dataError }}
            </div>
            <component
                v-else
                v-bind:is="chartType"
                :chart-data="chartData"
                :height="null"
                :width="null"
                class="flex flex-wrap items-center chart-component"
                :scheme="scheme"
            ></component>
        </div>
    </div>
</template>

<script>
    import BarChart from "../../../components/charts/barChart"
    import BarStackedChart from "../../../components/charts/barStackedChart"
    import { call, get } from 'vuex-pathify'
    import { camelCase, cloneDeep, ceil, isFinite, sortBy, uniqBy } from "lodash"
    import DoughnutChart from "../../../components/charts/doughnutChart"
    import GaugeChart from "../../../components/charts/gaugeChart"
    import HeroNumberChart from "../../../components/charts/heroNumberChart"
    import LineChart from "../../../components/charts/lineChart"
    import LineStackedChart from "../../../components/charts/lineStackedChart"
    import FunnelChart from "../../../components/charts/funnelChart"
    import PieChart from "../../../components/charts/pieChart"
    import TableChart from "../../../components/charts/tableChart"

    export default {
        props: {
            value: {
                type: Object,
                required: true,
            },

            scheme: {
                type: String,
                default: 'dark'
            },
        },

        components: {
            BarChart,
            BarStackedChart,
            DoughnutChart,
            FunnelChart,
            GaugeChart,
            HeroNumberChart,
            LineChart,
            LineStackedChart,
            PieChart,
            TableChart
        },

        data() {
            return {
                chart: this.value,

                chartData: {
                    labels: [],
                    datasets: [],
                    isInverted: false,
                },

                dataError: null,

                loading: true,
            }
        },

        computed: {
            user: get('auth/user'),

            allTags() {
                return [...this.chart.tags.category, ...this.chart.tags.channel].sort()
            },

            chartType() {
                return `${camelCase(this.chart.type.slug)}Chart`
            },

            hasChartData() {
                return this.chartData.labels.length > 0 && this.chartData.datasets.length
            },

            hasDataError() {
                return this.dataError !== null
            },

            isLoading() {
                return this.loading ? true : false
            },

            sortedTags() {
                return this.allTags.sort((a,b) => (a.slug > b.slug) ? 1 : ((b.slug > a.slug) ? -1 : 0))
            },
        },

        watch: {
            value: async function (newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.loading = true
                    this.chart = newValue

                    await this.loadData()

                    this.loading = false
                }
            },

            'value.start_date': async function (newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.loading = true

                    await this.loadData()

                    this.loading = false
                }
            },

            'value.last_updated': async function (newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.loading = true

                    await this.loadData()

                    this.loading = false
                }
            },
        },

        async mounted () {
            await this.loadData()

            this.loading = false
        },

        methods: {
            loadChartData: call('charts/loadChartData'),

            formatData() {
                let labels = []
                let datasets = []

                if (this.chart.dimensions && this.chart.dimensions.length > 0) {
                    labels = cloneDeep(this.chart.dimensions[0].labels)
                }

                if (this.chart.datasets) {
                    datasets = cloneDeep(this.chart.datasets)
                }

                for (const dataset of datasets) {
                    if (dataset.data_category === 'number' || dataset.data_category === null) {
                        if (this.chart.number_format === 'float') {
                            dataset.data = this.formatToFloat(dataset.data, dataset.data_format)
                        }

                        if (this.chart.number_format === 'integer') {
                            dataset.data = this.formatToInteger(dataset.data, dataset.data_format)
                        }
                    }
                }

                return {
                    labels: labels,
                    datasets: datasets,
                    isInverted: this.chart.is_inverted,
                }
            },

            formatToFloat(data, data_format) {
                let results = data.map(value => {
                    if (!isFinite(value)) {
                        return null
                    }

                    if (data_format === 'ratio' || (['gauge', 'hero-number'].includes(this.chart.type.slug) && value < 1)) {
                        value = value * 100
                    }

                    return ceil(value, 2)
                })

                return results
            },

            formatToInteger(data, data_format) {
                return data.map(value => {
                    if (!isFinite(value)) {
                        return null
                    }

                    if (data_format === 'ratio') {
                        value = value * 100
                    }

                    return ceil(value, 0)
                })
            },

            async loadData() {
                try {
                    await this.loadChartData(this.chart)

                    this.dataError = null
                } catch (error) {
                    if (error.name === "MetricDataException") {
                        this.dataError = error.message
                    }
                }

                this.chartData = this.formatData()
            },
        }
    }
</script>
